import createClient from 'openapi-fetch';
import type { paths } from './schema';
import { clearTokens, getTokens, getViewAs, setTokens, type TokenPair } from './auth';

/**
 * Клиент API. Все запросы к нашему бэкенду идут через `authFetch`:
 * Bearer, X-View-As, единственный одновременный refresh и повтор запроса после 401.
 * Пути в схеме уже содержат `/api/v1`, поэтому baseUrl — origin (vite-прокси в dev).
 */

export const API_PREFIX = '/api/v1';

/**
 * Срок ответа на чтение (GET) и вход. В мобильной сети ответ может не прийти совсем — без ошибки;
 * без срока запрос висит вечно, а экран загрузки ждёт его. По сроку запрос обрывается с TimeoutError
 * и повторяется по правилам запроса. Записи (POST/PATCH…) без срока: в них загрузки фото.
 */
export const REQUEST_TIMEOUT_MS = 15_000;

function timeoutError(): DOMException {
  return new DOMException('Request timed out', 'TimeoutError');
}

/** Запрос с тем же сигналом отмены плюс срок ответа. Таймер не снимается: abort завершённого запроса ничего не делает. */
function withDeadline(req: Request, ms = REQUEST_TIMEOUT_MS): Request {
  const ctrl = new AbortController();
  const outer = req.signal;
  if (outer.aborted) ctrl.abort(outer.reason);
  else outer.addEventListener('abort', () => ctrl.abort(outer.reason), { once: true });
  setTimeout(() => ctrl.abort(timeoutError()), ms);
  return new Request(req, { signal: ctrl.signal });
}

function deadlineSignal(ms = REQUEST_TIMEOUT_MS): AbortSignal {
  const ctrl = new AbortController();
  setTimeout(() => ctrl.abort(timeoutError()), ms);
  return ctrl.signal;
}

let baseUrl = '';
let onAuthLost: (() => void) | null = null;
let refreshing: Promise<boolean> | null = null;

export function configureApi(opts: { baseUrl?: string; onAuthLost?: () => void }): void {
  if (opts.baseUrl !== undefined) baseUrl = opts.baseUrl.replace(/\/$/, '');
  if (opts.onAuthLost !== undefined) onAuthLost = opts.onAuthLost;
}

export function apiUrl(path: string): string {
  return `${baseUrl}${path}`;
}

/** Новый ключ идемпотентности (uuid v4). */
export function newIdempotencyKey(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  // Фолбэк для старых WebView без randomUUID.
  const b = new Uint8Array(16);
  c.getRandomValues(b);
  b[6] = ((b[6] ?? 0) & 0x0f) | 0x40;
  b[8] = ((b[8] ?? 0) & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

function isAuthPath(url: string): boolean {
  const path = new URL(url, 'http://x').pathname;
  return path.startsWith(`${API_PREFIX}/auth/`);
}

function withAuthHeaders(req: Request): Request {
  const headers = new Headers(req.headers);
  const tokens = getTokens();
  if (tokens && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${tokens.access}`);
  const viewAs = getViewAs();
  // Явный X-View-As запроса важнее текущего (действия очереди); пустой — «свой кабинет», не отправляется.
  if (headers.has('X-View-As')) {
    if (!headers.get('X-View-As')) headers.delete('X-View-As');
  } else if (viewAs) headers.set('X-View-As', viewAs);
  return new Request(req, { headers });
}

async function doRefresh(): Promise<boolean> {
  const tokens = getTokens();
  if (!tokens) return false;
  try {
    const res = await fetch(apiUrl(`${API_PREFIX}/auth/refresh`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: tokens.refresh }),
      // Зависший refresh держал бы все запросы, которые ждут новый токен.
      signal: deadlineSignal(),
    });
    if (!res.ok) {
      // 401/403 — refresh отозван или истёк. Сетевые/5xx — не разлогиниваем.
      if (res.status === 401 || res.status === 403) {
        clearTokens();
        onAuthLost?.();
      }
      return false;
    }
    setTokens((await res.json()) as TokenPair);
    return true;
  } catch {
    return false;
  }
}

/** Обновить access-токен; параллельные вызовы ждут один и тот же запрос. */
export function refreshTokens(): Promise<boolean> {
  refreshing ??= doRefresh().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

/** fetch с авторизацией и одним повтором после refresh. */
export async function authFetch(input: Request): Promise<Response> {
  const retryCopy = input.clone();
  const res = await fetch(withAuthHeaders(input));
  if (res.status !== 401 || isAuthPath(input.url) || !getTokens()) return res;
  const ok = await refreshTokens();
  if (!ok) return res;
  return fetch(withAuthHeaders(retryCopy));
}

/** fetch клиента openapi-fetch: origin из configureApi, срок ответа для чтения и входа (SSE идёт мимо — через authFetch). */
export function apiFetch(req: Request): Promise<Response> {
  let r = req;
  // baseUrl задаётся в рантайме через configureApi — переписываем origin, если он задан.
  if (baseUrl) {
    const u = new URL(req.url);
    r = new Request(`${baseUrl}${u.pathname}${u.search}`, req);
  }
  return authFetch(r.method === 'GET' || isAuthPath(r.url) ? withDeadline(r) : r);
}

/** Типизированный клиент openapi-fetch. */
export const api = createClient<paths>({
  // Абсолютный origin страницы: Request с относительным URL не создаётся вне браузера (тесты, воркеры).
  baseUrl: typeof location !== 'undefined' && location.origin && location.origin !== 'null' ? location.origin : '',
  fetch: apiFetch,
});

export type ApiClient = typeof api;
